import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, LogOut, Coins } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Logo, ThemeToggle } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";
import { num } from "@/lib/api";
import { cn } from "@/lib/utils";

const NavList = ({ items, onNavigate, onLogout }) => (
  <nav className="flex flex-col gap-1">
    {items.map(({ to, label, icon: Icon, end }) => (
      <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${to.split("/").filter(Boolean).join("-")}`}
        className={({ isActive }) => cn("group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
          isActive ? "bg-primary/12 text-primary bg-primary/10" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
        <Icon className="h-4 w-4 transition-transform group-hover:scale-110" />{label}
      </NavLink>
    ))}
    <button onClick={onLogout} data-testid="nav-logout" className="mt-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-rose-500/10 hover:text-rose-500">
      <LogOut className="h-4 w-4" />Çıkış
    </button>
  </nav>
);

export const CreditPill = () => {
  const { credits } = useAuth();
  return (
    <Link to="/panel/satin-al" data-testid="nav-credit-balance" className="flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/20">
      <Coins className="h-4 w-4" />
      <span className="hidden sm:inline text-xs font-medium text-muted-foreground">Kalan Kredi</span>
      <span data-testid="credit-available">{num(credits?.available)}</span>
    </Link>
  );
};

export default function AppShell({ items, badge, showCredits }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/giris"); };
  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card/50 p-5 backdrop-blur-xl lg:flex">
        <Logo to={items[0].to} />
        {badge && <span className="mt-3 w-fit rounded-full bg-fuchsia-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-fuchsia-400">{badge}</span>}
        <div className="mt-8 flex-1 overflow-y-auto"><NavList items={items} onLogout={doLogout} /></div>
        <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-xs">
          <div className="font-semibold truncate">{user?.first_name} {user?.last_name}</div>
          <div className="truncate text-muted-foreground">{user?.email}</div>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="glass sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-x-0 border-t-0 px-4 sm:px-8">
          <div className="flex items-center gap-2 lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72 p-5">
                <Logo to={items[0].to} />
                <div className="mt-8"><NavList items={items} onNavigate={() => setOpen(false)} onLogout={doLogout} /></div>
              </SheetContent>
            </Sheet>
            <span className="font-display font-bold sm:hidden">{badge || ""}</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            {showCredits && <CreditPill />}
            {user?.role === "ADMIN" && !badge && <Button asChild variant="outline" size="sm" data-testid="go-admin-button"><Link to="/admin">Admin</Link></Button>}
            {badge && <Button asChild variant="outline" size="sm" data-testid="go-panel-button"><Link to="/panel">Kullanıcı Paneli</Link></Button>}
            <ThemeToggle />
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10 animate-fade-up"><Outlet /></main>
      </div>
    </div>
  );
}
